/**
 * ISG Knowledge Platform — Main Entry Point
 */
import '@isg/ui-kit/css/tokens.css';
import '@isg/ui-kit/css/reset.css';
import '@isg/ui-kit/css/components.css';
import '@isg/ui-kit/css/layout.css';
import './styles/tokens.css';
import './styles/pages.css';

import { router, store, auth, api, checkEnvBanner } from '@isg/ui-kit';
import { renderNavigation, renderMobileHeader, createSidebarOverlay } from '@isg/ui-kit';
import { applyBranding, loadBranding } from '@isg/ui-kit';
import { checkTestMode, renderTestBanner } from '@isg/ui-kit';

/* SSO: capture handoff from intranet broker */
auth.captureSSOHandoff();

/**
 * Navigation configuration — Vault-specific.
 */
/** Documents management is for platform admins + vault tech/marketing
 *  admins only — mirrors the backend guard on /api/admin/documents. */
/** Cross-reference tool is in the same testing cohort as the AI assistant:
 *  platform admin, vault admin, vault tech_admin. The BACKEND is the authority
 *  (it also honours CROSSREF_TOOL_ENABLED); this only decides whether to show
 *  the nav entry, and the page itself re-asks the server before rendering. */
export function canUseCrossref(user) {
  // The SERVER decides. `loadToolCapabilities()` asks it before the shell is
  // built, because the gate is a policy plus an env flag and a client that
  // decides for itself drifts from it the moment the flag moves — which is
  // precisely what happened when these tools were opened to all vault users
  // on 2026-08-13: the API let them in and this function kept the nav hidden.
  const caps = store.get('toolCapabilities');
  if (caps) return !!(caps.part_lookup_enabled || caps.crossref_enabled);
  // Pre-answer fallback only (the fetch failed, or ran before this call).
  // Deliberately the RESTRICTIVE guess: showing a nav entry that 403s is worse
  // than briefly hiding one, and the page re-asks the server anyway.
  if (!user) return false;
  if (user.role === 'admin') return true;
  const v = user.app_access && user.app_access.vault;
  return !!v && ['admin', 'tech_admin'].includes(v.role);
}

/** A failed probe silently REMOVES nav entries, so it must not be silent.
 *  This is the difference between "the server said no" and "we never asked". */
function probeFailed(err, which) {
  console.warn(`[nav] ${which} capabilities probe failed — Part Search / `
    + 'Cross-Reference may be hidden from users who should see them. '
    + 'Check the request path (ui-kit already prefixes /api).', err);
}

/** Ask the server who may use Part Search / Cross-Reference. Never throws —
 *  a failed probe leaves `toolCapabilities` unset and the fallback applies. */
async function loadToolCapabilities() {
  try {
    // ⚠️ NO `/api` PREFIX. ui-kit's api client already prepends BASE_URL='/api',
    // so '/api/part/capabilities' requests '/api/api/part/capabilities' → 404.
    // That is exactly what shipped on 2026-08-13 and it hid Part Search and
    // Cross-Reference from every non-admin user for ~an hour: the 404 was
    // swallowed by the catch below, `toolCapabilities` stayed unset, and the
    // restrictive fallback in canUseCrossref did what it was designed to do.
    // Every other service in this app calls api.get('/crossref/...').
    const [part, xref] = await Promise.all([
      api.get('/part/capabilities').catch((e) => { probeFailed(e, 'part'); return null; }),
      api.get('/crossref/capabilities').catch((e) => { probeFailed(e, 'crossref'); return null; }),
    ]);
    if (part || xref) {
      store.set('toolCapabilities', {
        part_lookup_enabled: !!(part && part.part_lookup_enabled),
        crossref_enabled: !!(xref && xref.crossref_enabled),
        confidential_available: !!((part && part.confidential_available)
                                || (xref && xref.confidential_available)),
      });
    }
  } catch { /* fallback applies */ }
}

export function canManageDocuments(user) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const v = user.app_access && user.app_access.vault;
  return !!v && ['tech_admin', 'marketing_admin'].includes(v.role);
}

function buildNavConfig(user) {
  const system = [];
  if (canManageDocuments(user)) {
    system.push({ path: '/documents', label: 'Documents', icon: 'file-text' });
  }
  system.push({ path: '/admin', label: 'Admin', icon: 'settings', adminOnly: true });
  return [
    {
      section: null,
      items: [
        { path: '/vault', label: 'The Vault', icon: 'book-open' },
        // Part Search and Cross-Reference sit at positions 2 and 4 of the
        // requested order, so the cohort gate is spread twice rather than
        // once — keep both spreads on the SAME condition, or the two halves
        // of one dataset can drift apart in the nav the way they did in the
        // API until 2026-08-13.
        ...(canUseCrossref(user)
          ? [{ path: '/part', label: 'Part Search', icon: 'search' }]
          : []),
        { path: '/knowledge', label: 'Knowledge Base', icon: 'library' },
        ...(canUseCrossref(user)
          ? [{ path: '/crossref', label: 'Cross-Reference', icon: 'git-compare' }]
          : []),
        { path: '/assistant', label: 'AI Assistant', icon: 'bot' },
        // Every named vault user gets this — unlike /part and /crossref there
        // is no cohort flag, so the entry needs no capabilities probe and
        // cannot drift from a policy the way those two did.
        { path: '/my', label: 'My Vault', icon: 'star' },
        { path: '/newsfeed', label: 'Newsfeed', icon: 'rss' },
      ],
    },
    { section: 'System', items: system },
  ];
}

// Lazy-load pages
const pages = {
  aiAssistant:   () => import('./pages/ai-assistant.js'),
  crossref:      () => import('./pages/crossref.js'),
  part:          () => import('./pages/part.js'),
  knowledgeBase: () => import('./pages/knowledge-base.js'),
  vault:         () => import('./pages/vault.js'),
  myVault:       () => import('./pages/my-vault.js'),
  documents:     () => import('./pages/documents.js'),
  newsfeed:      () => import('./pages/newsfeed.js'),
  admin:         () => import('./pages/admin.js'),
  login:         () => import('./pages/login.js'),
};

/**
 * Render the portal shell: accent stripe + sidebar + main content area.
 */
function renderAppShell() {
  const app = document.getElementById('app');
  app.innerHTML = '';

  const stripe = document.createElement('div');
  stripe.className = 'accent-stripe';
  app.appendChild(stripe);

  const layout = document.createElement('div');
  layout.className = 'app-layout';

  const mobileHeader = renderMobileHeader({ appTitle: 'ISG Knowledge' });
  const overlay = createSidebarOverlay();

  const sidebar = renderNavigation({
    navConfig: buildNavConfig(store.get('user')),
    appTitle: 'Knowledge',
    fallbackInitial: 'K',
  });

  const main = document.createElement('main');
  main.className = 'main-content';
  main.id = 'main-content';

  layout.appendChild(mobileHeader);
  layout.appendChild(overlay);
  layout.appendChild(sidebar);
  layout.appendChild(main);
  app.appendChild(layout);

}

/**
 * Render a page module into #main-content.
 */
// The page currently mounted, so its `destroy()` can run before the next one
// mounts. Clearing `main.innerHTML` removes the DOM (and its listeners) but NOT
// what a page attached elsewhere: an in-flight AbortController, a debounce
// timer, a listener on `document`. Without this hook, `export function destroy`
// is code that looks like cleanup and never runs.
let currentModule = null;

async function renderPage(loader, params = {}) {
  const main = document.getElementById('main-content');
  if (!main) return;

  if (currentModule && typeof currentModule.destroy === 'function') {
    try { currentModule.destroy(); } catch (e) { console.error('page destroy failed', e); }
  }
  currentModule = null;

  main.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:50vh">
      <div class="spinner"></div>
    </div>
  `;

  try {
    const module = await loader();
    main.innerHTML = '';
    currentModule = module;
    await module.render(main, params);
  } catch (err) {
    console.error('Page render error:', err);
    main.innerHTML = `
      <div class="main-content-inner">
        <div class="empty-state">
          <p style="color: var(--red-600); font-weight: 500;">Something went wrong</p>
          <p>${err.message || 'Failed to load page.'}</p>
          <button class="btn btn-primary mt-4" onclick="location.reload()">Reload</button>
        </div>
      </div>
    `;
  }
}

/**
 * Initialize the application.
 */
async function init() {
  await loadBranding().catch(() => {});
  const hasSession = await auth.checkSession();

  router
    // '/' is REDIRECTED to /vault in the before hook below; this handler is
    // the fallback for if that ever stops firing — landing on the Vault is
    // the intent, so failing to the assistant would be the wrong default.
    .on('/', (params) => renderPage(pages.vault, params))
    .on('/assistant', (params) => renderPage(pages.aiAssistant, params))
    .on('/crossref', (params) => renderPage(pages.crossref, params))
    .on('/part', (params) => renderPage(pages.part, params))
    .on('/knowledge', (params) => renderPage(pages.knowledgeBase, params))
    .on('/knowledge/:id', (params) => renderPage(pages.knowledgeBase, params))
    .on('/vault', (params) => renderPage(pages.vault, params))
    .on('/my', (params) => renderPage(pages.myVault, params))
    .on('/my/:folder', (params) => renderPage(pages.myVault, params))
    .on('/documents', (params) => renderPage(pages.documents, params))
    .on('/vault/new', (params) => renderPage(pages.vault, { ...params, newArticle: true }))
    .on('/vault/edit/:id', (params) => renderPage(pages.vault, { ...params, edit: true }))
    .on('/vault/:id', (params) => renderPage(pages.vault, params))
    .on('/newsfeed', (params) => renderPage(pages.newsfeed, params))
    .on('/newsfeed/:id', (params) => renderPage(pages.newsfeed, params))
    .on('/admin', (params) => renderPage(pages.admin, params))
    .on('/login', (params) => renderPage(pages.login, params));

  router.before((path) => {
    // An AUTHENTICATED user must never be left on the login page. Waving
    // /login through unconditionally renders the login FORM inside the app
    // shell — the app looks signed in and signed out at once, and only a
    // manual reload clears it. Not an edge case: api.js bounces here on any
    // 401 while the httpOnly cookie usually survives, so the reload lands
    // authenticated; the SSO broker's ?next and plain bookmarks reach it too.
    // Fixed in isg-app-template 2026-07-18; back-propagated 2026-07-31.
    if (path === '/login' && store.get('isAuthenticated')) {
      router.navigate('/');
      return false;
    }
    const publicPaths = ['/login', '/register'];
    if (!publicPaths.includes(path) && !store.get('isAuthenticated')) {
      router.navigate('/login');
      return false;
    }
    // The Vault is the landing page. Redirect rather than render, so the URL
    // is canonical and the sidebar highlights The Vault — ui-kit matches the
    // nav entry against the CURRENT PATH, so rendering the vault at '/' would
    // land people on the right page with nothing lit up. This one hook covers
    // every route in: the PWA start_url, ui-kit's post-login homeUrl, the
    // /login bounce above, and a plain bookmark.
    if (path === '/') {
      // replaceState, NOT router.navigate — navigate() PUSHES, so '/' would
      // stay in the history stack and Back from /vault would land on it and
      // be redirected straight forward again. Replacing it means Back reaches
      // whatever the user actually came from.
      history.replaceState(null, '', '/vault');
      router.resolve();
      return false;
    }
    return true;
  });

  router.after(() => {
    setTimeout(() => {
      applyBranding();
      renderTestBanner();
    }, 50);
  });

  checkTestMode();

  if (!hasSession) {
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const module = await pages.login();
    module.render(document.getElementById('main-content'));
    if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
      window.history.replaceState(null, '', '/login');
    }
    applyBranding();
  } else {
    // Before the shell: the nav is built once from this answer.
    await loadToolCapabilities();
    renderAppShell();
    checkEnvBanner(); // beta/test banner (no-op on live)
    await router.resolve();
    applyBranding();
    renderTestBanner();
  }
}

init().catch((err) => {
  console.error('Failed to initialize app:', err);
  document.getElementById('app').innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;flex-direction:column;gap:1rem;">
      <p>Failed to load the application. Please refresh.</p>
      <button class="btn btn-primary" onclick="location.reload()">Reload</button>
    </div>
  `;
});
